import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './KeyRequestDashboard.css';

const KeyRequestDashboard = ({ currentUser, gameId = null }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Determine if user is indie dev or creator
  const isIndieDev = currentUser?.accountType === 'indie_dev';

  useEffect(() => {
    fetchRequests();
  }, [filter, gameId]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url;
      
      if (gameId) {
        // Fetch requests for specific game (indie dev view)
        url = `/api/key-requests/game/${gameId}?status=${filter}`;
      } else if (isIndieDev) {
        // This would need a new endpoint to get all requests for all games by this dev
        // For now, return empty
        setRequests([]);
        setLoading(false);
        return;
      } else {
        // Fetch creator's own requests
        url = `/api/key-requests/my-requests?status=${filter}`;
      }

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setRequests(response.data.requests);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Approve this key request? Make sure to send the key via direct message after approving.')) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/key-requests/${requestId}/approve`, 
        { reviewNote },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert('Request approved! Please send the game key to the creator via direct message.');
      setSelectedRequest(null);
      setReviewNote('');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Reject this key request?')) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/key-requests/${requestId}/reject`,
        { reviewNote },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      alert('Request rejected');
      setSelectedRequest(null);
      setReviewNote('');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async (requestId) => {
    if (!window.confirm('Withdraw this key request?')) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/key-requests/${requestId}/withdraw`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Request withdrawn');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to withdraw request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkFulfilled = async (requestId) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/key-requests/${requestId}/fulfill`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Request marked as fulfilled');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to mark as fulfilled');
    } finally {
      setActionLoading(false);
    }
  };

  const viewDetails = (request) => {
    setSelectedRequest(request);
    setReviewNote('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffa500',
      approved: '#00d9ff',
      rejected: '#ff6b6b',
      withdrawn: '#888',
      fulfilled: '#4caf50'
    };
    return colors[status] || '#888';
  };

  if (loading) {
    return <div className="key-request-dashboard loading">Loading requests...</div>;
  }

  return (
    <div className="key-request-dashboard">
      <div className="dashboard-header">
        <h2>{isIndieDev ? 'Incoming Key Requests' : 'My Key Requests'}</h2>
        
        <div className="filter-tabs">
          <button
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={filter === 'approved' ? 'active' : ''}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            className={filter === 'rejected' ? 'active' : ''}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
          <button
            className={filter === 'fulfilled' ? 'active' : ''}
            onClick={() => setFilter('fulfilled')}
          >
            Fulfilled
          </button>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="no-requests">
          <p>No {filter} requests found</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map(request => (
            <div key={request._id} className="request-card">
              <div className="request-header-row">
                <div className="request-info">
                  {isIndieDev ? (
                    <>
                      <h3>{request.creatorId?.username || 'Unknown User'}</h3>
                      <p className="platform-info">
                        {request.platformInfo.primaryPlatform === 'twitch' && (
                          <>Twitch: {request.platformInfo.twitchUsername} ({request.platformInfo.twitchFollowers} followers)</>
                        )}
                        {request.platformInfo.primaryPlatform === 'youtube' && (
                          <>YouTube: {request.platformInfo.youtubeSubscribers} subscribers</>
                        )}
                        {request.platformInfo.primaryPlatform === 'both' && (
                          <>Twitch + YouTube: {(request.platformInfo.twitchFollowers || 0) + (request.platformInfo.youtubeSubscribers || 0)} total</>
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <h3>{request.indieGameId?.name || 'Game'}</h3>
                      <p className="dev-info">
                        Developer: {request.indieDevId?.username || 'Unknown'}
                      </p>
                    </>
                  )}
                </div>
                
                <div className="request-meta">
                  <span 
                    className="status-badge" 
                    style={{ backgroundColor: getStatusColor(request.status) }}
                  >
                    {request.status}
                  </span>
                  <span className="request-date">{formatDate(request.createdAt)}</span>
                </div>
              </div>

              <div className="request-preview">
                <p className="message-preview">{request.message.substring(0, 150)}...</p>
              </div>

              <div className="request-actions">
                <button onClick={() => viewDetails(request)} className="view-details-btn">
                  View Details
                </button>

                {isIndieDev && request.status === 'pending' && (
                  <>
                    <button onClick={() => viewDetails(request)} className="approve-btn">
                      Review & Approve
                    </button>
                    <button onClick={() => { setSelectedRequest(request); }} className="reject-btn">
                      Reject
                    </button>
                  </>
                )}

                {isIndieDev && request.status === 'approved' && (
                  <button onClick={() => handleMarkFulfilled(request._id)} className="fulfill-btn">
                    Mark as Fulfilled
                  </button>
                )}

                {!isIndieDev && ['pending', 'approved'].includes(request.status) && (
                  <button onClick={() => handleWithdraw(request._id)} className="withdraw-btn">
                    Withdraw Request
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {selectedRequest && (
        <div className="request-details-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Request Details</h2>
              <button onClick={() => setSelectedRequest(null)} className="close-btn">&times;</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h3>Creator Information</h3>
                <div className="detail-grid">
                  <div><strong>Username:</strong> {selectedRequest.creatorId?.username}</div>
                  <div><strong>Email Verified:</strong> {selectedRequest.validationData?.emailVerified ? 'Yes' : 'No'}</div>
                  <div><strong>Account Age:</strong> {selectedRequest.validationData?.accountAge} days</div>
                  <div><strong>Vouch Score:</strong> {selectedRequest.validationData?.vouchScore || 0}</div>
                  <div><strong>Followers:</strong> {selectedRequest.validationData?.followerCount || 0}</div>
                  <div><strong>Warnings:</strong> {selectedRequest.validationData?.warningCount || 0}</div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Platform Details</h3>
                {selectedRequest.platformInfo.twitchUsername && (
                  <div className="platform-details">
                    <strong>Twitch:</strong> {selectedRequest.platformInfo.twitchUsername}
                    <br />Followers: {selectedRequest.platformInfo.twitchFollowers}
                    {selectedRequest.platformInfo.twitchVerified && <span className="verified-badge">✓ Verified</span>}
                  </div>
                )}
                {selectedRequest.platformInfo.youtubeChannel && (
                  <div className="platform-details">
                    <strong>YouTube:</strong> <a href={selectedRequest.platformInfo.youtubeChannel} target="_blank" rel="noopener noreferrer">Channel Link</a>
                    <br />Subscribers: {selectedRequest.platformInfo.youtubeSubscribers}
                    {selectedRequest.platformInfo.youtubeVerified && <span className="verified-badge">✓ Verified</span>}
                  </div>
                )}
                <div><strong>Average Viewers/Views:</strong> {selectedRequest.platformInfo.averageViewers}</div>
                <div><strong>Content Type:</strong> {selectedRequest.platformInfo.contentType?.join(', ')}</div>
              </div>

              <div className="detail-section">
                <h3>Content Portfolio</h3>
                {selectedRequest.portfolio.recentContent.map((content, idx) => (
                  <div key={idx} className="portfolio-link">
                    <a href={content.url} target="_blank" rel="noopener noreferrer">Link {idx + 1}</a>
                    {content.description && <span> - {content.description}</span>}
                  </div>
                ))}
              </div>

              <div className="detail-section">
                <h3>Streaming Commitment</h3>
                <div><strong>Schedule:</strong> {selectedRequest.commitment.streamingSchedule}</div>
                {selectedRequest.commitment.estimatedStreamDate && (
                  <div><strong>Estimated Date:</strong> {formatDate(selectedRequest.commitment.estimatedStreamDate)}</div>
                )}
              </div>

              <div className="detail-section">
                <h3>Creator's Message</h3>
                <p className="creator-message">{selectedRequest.message}</p>
              </div>

              {selectedRequest.customResponses && selectedRequest.customResponses.length > 0 && (
                <div className="detail-section">
                  <h3>Additional Responses</h3>
                  {selectedRequest.customResponses.map((response, idx) => (
                    <div key={idx} className="custom-response">
                      <strong>Q: {response.question}</strong>
                      <p>A: {response.answer}</p>
                    </div>
                  ))}
                </div>
              )}

              {selectedRequest.reviewNote && (
                <div className="detail-section">
                  <h3>Review Note</h3>
                  <p>{selectedRequest.reviewNote}</p>
                </div>
              )}

              {isIndieDev && selectedRequest.status === 'pending' && (
                <div className="detail-section">
                  <h3>Review Action</h3>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Optional note for the creator..."
                    rows="3"
                    maxLength="500"
                  />
                  <div className="review-actions">
                    <button 
                      onClick={() => handleApprove(selectedRequest._id)}
                      disabled={actionLoading}
                      className="approve-action-btn"
                    >
                      {actionLoading ? 'Processing...' : 'Approve & Send Key'}
                    </button>
                    <button 
                      onClick={() => handleReject(selectedRequest._id)}
                      disabled={actionLoading}
                      className="reject-action-btn"
                    >
                      {actionLoading ? 'Processing...' : 'Reject Request'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyRequestDashboard;
